import { useCallback, useEffect, useRef, useState } from "react";

// Detects when a newer build of the app has been deployed, so PWA-installed / long-lived
// browser sessions can be told to refresh. We compare the hashed asset bundle referenced by a
// freshly-fetched index.html against the one the app booted with (each deploy produces new
// hashed filenames). No caching service worker is introduced — this stays safe for the SPA.
const POLL_MS = 5 * 60 * 1000;

async function fetchBundleSignature() {
  try {
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const html = await res.text();
    const hashed = html.match(/\/static\/(?:js|css)\/[^"']+?\.[a-f0-9]{8,}\.(?:js|css)/g);
    if (hashed && hashed.length) return [...new Set(hashed)].sort().join("|");
    const scripts = html.match(/<script[^>]+src=["'][^"']+["'][^>]*>/g);
    return scripts ? scripts.join("|") : null;
  } catch {
    return null;
  }
}

export function useAppUpdate() {
  const boot = useRef(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const check = useCallback(async () => {
    const sig = await fetchBundleSignature();
    if (!sig) return;
    if (boot.current == null) {
      boot.current = sig;
      return;
    }
    if (sig !== boot.current) setUpdateAvailable(true);
  }, []);

  useEffect(() => {
    check();
    const t = setInterval(check, POLL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [check]);

  const reload = useCallback(() => {
    if ("serviceWorker" in navigator && navigator.serviceWorker.getRegistrations) {
      navigator.serviceWorker
        .getRegistrations()
        .then((rs) => rs.forEach((r) => r.update && r.update()))
        .catch(() => {});
    }
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}
